import { Icon, MenuItem, Select, Switch, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { compactSelectSx } from "./worksheetStyles";
import WorksheetHaulingFields from "./WorksheetHaulingFields";
import WorksheetIconButton from "./WorksheetIconButton";
import { useState } from "react";

export const TRUCKING_UNIT_OPTIONS = ["Hours", "Tons", "Yards", "Loads"] as const;

type TruckingUnit = (typeof TRUCKING_UNIT_OPTIONS)[number];

const RATE_FIELD_BY_UNIT: Record<TruckingUnit, keyof WorksheetItemInput["costRows"][number]> = {
  Hours: "truckRateHours",
  Tons: "truckRateTons",
  Yards: "truckRateYards",
  Loads: "truckRateLoads",
};

type WorksheetTruckingRowFieldsProps = {
  index: number;
  isReadOnly?: boolean;
  plannerActive: boolean;
  computedTrucks?: number;
  computedQuantity?: number;
};

export default function WorksheetTruckingRowFields({
  index,
  isReadOnly,
  plannerActive,
  computedTrucks,
  computedQuantity,
}: WorksheetTruckingRowFieldsProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const row = (useWatch({ control, name: `costRows.${index}` }) ??
    {}) as WorksheetItemInput["costRows"][number];
  const unit = (TRUCKING_UNIT_OPTIONS as readonly string[]).includes(row.truckingUnit ?? "")
    ? (row.truckingUnit as TruckingUnit)
    : "Hours";

  const [plannerOpen, setPlannerOpen] = useState(false);

  const setPlannerActive = (active: boolean) => {
    setValue(`costRows.${index}.quantitySource`, active ? "trucking" : "manual", {
      shouldDirty: true,
    });
    setValue(`costRows.${index}.quantityOverridden`, false, { shouldDirty: true });
  };

  const handleUnitChange = (nextUnit: TruckingUnit) => {
    const currentRateField = RATE_FIELD_BY_UNIT[unit];
    const nextRateField = RATE_FIELD_BY_UNIT[nextUnit];

    setValue(`costRows.${index}.${currentRateField}`, row.rate, { shouldDirty: true });
    setValue(`costRows.${index}.truckingUnit`, nextUnit, { shouldDirty: true });
    setValue(`costRows.${index}.rate`, (row[nextRateField] as string | undefined) ?? "", {
      shouldDirty: true,
    });

    if (nextUnit !== "Hours") {
      setValue(`costRows.${index}.hoursPerTruck`, "", { shouldDirty: true });
    }
  };

  return (
    <>
      <Controller
        control={control}
        name={`costRows.${index}.trucksNeeded`}
        render={({ field }) => (
          <UiInput
            value={plannerActive ? computedTrucks ?? "" : field.value}
            readOnly={isReadOnly || plannerActive}
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />
      {unit === "Hours" ? (
        <Controller
          control={control}
          name={`costRows.${index}.hoursPerTruck`}
          render={({ field }) => (
            <UiInput
              value={plannerActive ? computedQuantity ?? "" : field.value}
              readOnly={isReadOnly || plannerActive}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      ) : (
        <Controller
          control={control}
          name={`costRows.${index}.quantity`}
          render={({ field }) => (
            <UiInput
              value={plannerActive ? computedQuantity ?? "" : field.value}
              readOnly={isReadOnly || plannerActive}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      )}
      <MDBox display="flex" alignItems="center" gap="2px" minWidth={0}>
        <Select
          value={unit}
          disabled={isReadOnly}
          sx={{ ...compactSelectSx, flex: 1, minWidth: 0 }}
          onChange={(event) => handleUnitChange(event.target.value as TruckingUnit)}
        >
          {TRUCKING_UNIT_OPTIONS.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: "11px" }}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {!isReadOnly && (
          <WorksheetIconButton
            icon="local_shipping"
            title="Open hauling planner"
            ariaLabel="Open hauling planner"
            onClick={() => {
              if (!plannerActive) setPlannerActive(true);
              setPlannerOpen(true);
            }}
          />
        )}
      </MDBox>

      <UiSideModal
        open={plannerOpen}
        onClose={() => setPlannerOpen(false)}
        title="Hauling Planner"
        width="498px"
        onConfirm={() => setPlannerOpen(false)}
        confirmLabel="Done"
      >
        <MDBox p="16px" display="flex" flexDirection="column" gap="12px">
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <UiTypography uiVariant="body-sm">Use hauling planner</UiTypography>
            <Switch
              size="small"
              checked={plannerActive}
              disabled={isReadOnly}
              onChange={(event) => setPlannerActive(event.target.checked)}
            />
          </MDBox>
          {plannerActive ? (
            <WorksheetHaulingFields index={index} isReadOnly={isReadOnly} />
          ) : (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
              Turn on the planner to calculate truck counts and billed quantities.
            </UiTypography>
          )}
        </MDBox>
      </UiSideModal>
    </>
  );
}
